/*
 * Collage Picture Products slider — NATIVE CSS SCROLL-SNAP (no Swiper).
 *
 * Replaces Swiper for this section. The row is a plain `overflow-x: auto`
 * container with `scroll-snap-type: x mandatory`, so dragging/swiping is handled
 * by the browser on the COMPOSITOR thread and never blocks the main thread (the
 * thread that has to answer taps — i.e. this is the INP win). Swiper cost ~29ms
 * of main-thread work per slider to construct; this costs ~0.
 *
 * This file only wires the things CSS can't do:
 *   - prev/next arrows            -> scrollBy(clientWidth)
 *   - segmented progress bar      -> derived from scrollLeft / clientWidth
 *   - desktop mouse click-drag    -> pointer-events shim (native overflow only
 *                                    drags via touch/trackpad, not mouse)
 *   - smooth-scroll polyfill      -> Safari < 15.4 lacks scroll-behavior
 *
 * Also keeps the lazy poster-video playback (unrelated to the slider).
 */
(function () {
  // Guard against the many duplicate <script> tags (one per collage section).
  if (window.__collageSlidingInit) return;
  window.__collageSlidingInit = true;

  var SUPPORTS_SMOOTH =
    typeof document !== 'undefined' &&
    'scrollBehavior' in document.documentElement.style;

  // Safari < 15.4 ignores scroll-behavior / {behavior:'smooth'}. Animate by hand
  // so arrow clicks glide instead of jumping.
  function scrollToLeft(el, left) {
    var max = el.scrollWidth - el.clientWidth;
    var target = Math.max(0, Math.min(left, max));

    if (SUPPORTS_SMOOTH) {
      el.scrollTo({ left: target, behavior: 'smooth' });
      return;
    }

    var start = el.scrollLeft;
    var delta = target - start;
    if (!delta) return;
    var duration = 320;
    var startTime = null;

    function step(now) {
      if (startTime === null) startTime = now;
      var p = Math.min((now - startTime) / duration, 1);
      // easeInOutQuad
      var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      el.scrollLeft = start + delta * eased;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initContainer(container) {
    if (container.dataset.snapReady === '1') return;
    var viewport = container.querySelector('.collage-swiper');
    if (!viewport) return;
    container.dataset.snapReady = '1';

    var prevBtn = container.querySelector('.collage-caret-left.prev');
    var nextBtn = container.querySelector('.collage-caret-right.next');
    var progress = container.querySelector('.collage-progress-indicators');

    var pages = 0;

    function pageCount() {
      // A "page" is one viewport-width of slides — matches the old
      // slidesPerGroup behaviour closely enough to be indistinguishable.
      var w = viewport.clientWidth;
      if (!w) return 1;
      return Math.max(1, Math.round(viewport.scrollWidth / w));
    }

    function activePage() {
      var max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 0) return 0;
      var ratio = viewport.scrollLeft / max;
      return Math.round(ratio * (pageCount() - 1));
    }

    function buildSegments() {
      if (!progress) return;
      var total = pageCount();
      if (pages === total) return;
      pages = total;

      progress.innerHTML = '';
      for (var i = 0; i < total; i++) {
        var seg = document.createElement('div');
        seg.className = 'collage-progress-segment';
        seg.setAttribute('data-index', i);
        (function (index) {
          seg.addEventListener('click', function () {
            scrollToLeft(viewport, index * viewport.clientWidth);
          });
        })(i);
        progress.appendChild(seg);
      }
    }

    function onUpdate() {
      buildSegments();

      if (progress) {
        var active = activePage();
        Array.prototype.forEach.call(progress.children, function (seg, i) {
          seg.classList.toggle('active', i === active);
        });
      }

      var max = viewport.scrollWidth - viewport.clientWidth;
      if (prevBtn) prevBtn.disabled = viewport.scrollLeft <= 1;
      if (nextBtn) nextBtn.disabled = viewport.scrollLeft >= max - 1;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToLeft(viewport, viewport.scrollLeft - viewport.clientWidth);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToLeft(viewport, viewport.scrollLeft + viewport.clientWidth);
      });
    }

    // passive: never blocks the compositor-driven scroll
    viewport.addEventListener('scroll', onUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(onUpdate).observe(viewport);
    } else {
      window.addEventListener('resize', onUpdate);
    }

    // ---- Desktop mouse click-drag shim ----
    // Native overflow scrolling covers touch, trackpad and wheel, but NOT
    // click-and-drag with a mouse. Swiper did, so reproduce it for parity.
    var down = false, startX = 0, startLeft = 0, moved = false;

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      viewport.scrollLeft = startLeft - dx;
    });

    function endDrag() {
      if (!down) return;
      down = false;
      viewport.classList.remove('is-dragging');
      // Re-snap to the nearest page once the class (and snapping) is restored.
      var w = viewport.clientWidth;
      if (w) scrollToLeft(viewport, Math.round(viewport.scrollLeft / w) * w);
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);

    // Suppress the click that ends a drag so it doesn't open a product page.
    viewport.addEventListener('click', function (e) {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    }, true);

    onUpdate();
  }

  function setup() {
    var containers = document.querySelectorAll('.collage-sliding-container');
    if (!containers.length) return;

    // Scrolling itself works with zero JS, so wiring can wait until the slider
    // is near the viewport — keeps layout reads off the critical path.
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(containers, initContainer);
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            initContainer(entry.target);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    Array.prototype.forEach.call(containers, function (c) { io.observe(c); });
  }

  // ---- Lazy autoplay for collage poster videos ----
  // The <video> renders WITHOUT `autoplay` and with preload="none", so its
  // multi-MB file isn't fetched during initial load. Start playback only when it
  // nears the viewport, and pause when it leaves to free the decoder. Muted
  // videos are allowed to play programmatically without a user gesture.
  // ES5 syntax throughout this file on purpose: Shopify only auto-minifies
  // JavaScript assets that are ES5 or lower, so const/let/arrows would silently
  // opt this file out of minification.
  function setupVideos() {
    var videos = document.querySelectorAll('.collage-poster-video');
    if (!videos.length) return;

    function playVideo(video) {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    // No IntersectionObserver (very old browsers): just play them all.
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(videos, playVideo);
      return;
    }

    var vio = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            playVideo(entry.target);
          } else {
            entry.target.pause();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    Array.prototype.forEach.call(videos, function (video) { vio.observe(video); });
  }

  // ---- Reset restored scroll offsets ----
  // These rows are `overflow-x: auto`, so the browser (notably iOS Safari)
  // records each one's scrollLeft and restores it on reload and on bfcache
  // restore. A row the shopper had swiped then comes back mid-way, looking
  // half-scrolled. Swiper never had this problem because it moved slides with
  // transforms — there was no scroll offset to restore. Product rows should
  // always open at the first card, so put them back.
  // ---- Reset restored scroll offsets (lazily) ----
  // These rows are `overflow-x: auto`, so iOS Safari records each one's
  // scrollLeft and restores it on reload and on bfcache restore — a row the
  // shopper had swiped comes back mid-way, looking half-scrolled. Swiper never
  // had this problem because it moved slides with transforms; there was no
  // scroll offset to restore.
  //
  // Correcting all rows in one sweep measured ~12ms, because every scrollLeft
  // write makes the scroller re-snap (scroll-snap-type: mandatory). Batching
  // reads before writes did NOT help — the cost is the writes themselves. So
  // correct each row only as it nears the viewport: a typical visit sees 3-4 of
  // them, and rows never scrolled to are never touched.
  var resetObserver = null;

  function armScrollReset() {
    var els = document.querySelectorAll('.collage-swiper');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) {
        if (els[i].scrollLeft) els[i].scrollLeft = 0;
      }
      return;
    }

    // Drop the previous generation so repeated bfcache restores don't stack up
    // observers holding references to rows that were never scrolled into view.
    if (resetObserver) resetObserver.disconnect();

    resetObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          var el = entry.target;
          if (!el.scrollLeft) return;
          // scroll-behavior: smooth would animate the correction and be visible.
          var prev = el.style.scrollBehavior;
          el.style.scrollBehavior = 'auto';
          el.scrollLeft = 0;
          el.style.scrollBehavior = prev;
        });
      },
      { rootMargin: '200px 0px' }
    );

    Array.prototype.forEach.call(els, function (el) { resetObserver.observe(el); });
  }

  var started = false;
  function boot() {
    if (started) return;
    started = true;
    setup();
    setupVideos();
  }
  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }

  // `pageshow` fires on the first load AND on every bfcache restore, and always
  // after Safari has restored offsets — so it is the only hook needed.
  window.addEventListener('pageshow', armScrollReset);
})();
